import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';
import s from './Button.module.css';
import { cx } from './cx.ts';
import t from './type.module.css';

export type ButtonVariant = 'primary' | 'leader' | 'ink' | 'outline' | 'ghost' | 'destructive';

/** C-03 Button (docs/design/components/C-03-button.md). */
export function Button({
  variant = 'leader',
  icon: Icon,
  size = 'regular',
  leader = variant === 'leader',
  fullWidth = false,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  icon?: LucideIcon;
  size?: 'regular' | 'small';
  /** The hairline leader (M-21); on by default for `leader`, optional on `ink`. */
  leader?: boolean;
  /** Stretches to the column, the icon at the far edge: a screen's single primary action (S-01). */
  fullWidth?: boolean;
}) {
  const compact = size === 'small';
  return (
    <button
      type={type}
      className={cx(
        s.button,
        s[variant],
        compact && s.small,
        fullWidth && s.full,
        Icon && variant !== 'ghost' && s.withIcon,
        compact ? t.bodyS : t.titleM,
        className,
      )}
      {...rest}
    >
      {leader && !rest.disabled && <span className={s.lead} aria-hidden="true" />}
      <span className={cx(fullWidth && s.grow)}>{children}</span>
      {Icon && variant !== 'ghost' && (
        <span className={s.end} aria-hidden="true">
          <Icon strokeWidth={2.5} />
        </span>
      )}
    </button>
  );
}
